// PaymentAgingPage — 支払突合表(年齢表) (2026-06-24, schema-v72 / hybrid redesign)
//
// freee の取引(deal)を 業者 × 決済期日(due_date)月 でクロス集計する年齢表。
// マトリクスは freee 真値を都度集計(D1 キャッシュなし)。業者→区分の割当のみ D1 に保存。
//
// レイアウト(ハイブリッド):
//   ① 上部に「区分 × 月」サマリーを固定表示（区分行クリック / セグメントで選択）
//   ② 下部に選択中の区分の業者明細(金額降順・主要業者のみ既定)
// 全体把握と詳細を同時に。スクロールは選択区分の中だけで最小化。
//
// デザイン: レインボー配色を廃し、ニュートラル + 単一アクセント(藍)で洗練。
//   freee Vibes / BudgetActual・SeibuCashLedger とトーン統一。絵文字不使用。

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Container,
  PageTitle,
  CardBase,
  HStack,
  VStack,
  Button,
  SelectBox,
  MessageBlock,
  InlineSpinner,
  Breadcrumbs,
} from '@freee_jp/vibes';
import {
  MdRefresh,
  MdEdit,
  MdAutoFixHigh,
  MdClose,
  MdPrint,
} from 'react-icons/md';
import {
  fetchPaymentAgingMatrix,
  fetchPaymentAgingVendors,
  savePaymentVendorCategory,
  bulkSavePaymentVendorCategory,
  clearPaymentVendorCategory,
  PAYMENT_CATEGORIES,
  PaymentAgingResponse,
  PaymentAgingCategoryGroup,
  PaymentAgingCell,
  PaymentAgingVendor,
  PaymentAgingVendorRow,
  // 2026-07-22 拡張: 動的カテゴリ / チェック済み / セル詳細 / 証憑 / 修正指示
  PaymentAgingCategoryDef,
  fetchPaymentAgingCategories,
  createPaymentAgingCategory,
  deletePaymentAgingCategory,
  reorderPaymentAgingCategories,
  setPaymentAgingCheck,
  fetchPaymentAgingCell,
  paymentAgingReceiptUrl,
  PaymentAgingDeal,
  createJournalCorrection,
  // 複式簿記表示 (仕訳レビュー形式) 用マスタ名称 map (module cache 済 = 初回のみ実 fetch)
  fetchAccountItemsMap,
  fetchSectionsMap,
  fetchTaxCodesMap,
  fetchItemsMap,
  fetchTagsMap,
  fetchAllFreeePartners,
  fetchWalletablesMap,
  JournalReviewDeal,
} from '../api/client';
import { dealToJournalGroup, NameMaps, JournalGroupData } from '../utils/journalTransform';
import { DealLedger, DEAL_LEDGER_CSS } from '../components/DealLedger';

// ===========================================================================
// 型 / 定数 / helper
// ===========================================================================

type StatusMode = 'both' | 'unsettled' | 'settled';
type PayDay = '25' | 'all';

// freee 取引(仕訳)詳細への deep-link
const freeeDealUrl = (id: number) => `https://secure.freee.co.jp/deals#deal_id=${id}`;

const MONTH_CHOICES = [3, 6, 9, 12];

const PREFS_KEY = 'freeeman.paymentAging.prefs.v3';
interface Prefs { months: number; statusMode: StatusMode; majorOnly: boolean; payDay: PayDay; }
const loadPrefs = (): Partial<Prefs> => {
  try { return JSON.parse(localStorage.getItem(PREFS_KEY) || '{}'); } catch { return {}; }
};
const savePrefs = (p: Prefs) => {
  try { localStorage.setItem(PREFS_KEY, JSON.stringify(p)); } catch { /* silent */ }
};

const currentYm = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const yen = (n: number | null | undefined): string =>
  n == null ? '' : `¥${Number(n).toLocaleString('ja-JP')}`;

const EMPTY_CELL: PaymentAgingCell = { unsettled: 0, settled: 0, due: 0, deal_ids: [], settled_deal_ids: [] };

// status に応じてセル / 小計から表示値を取り出す
const cellValue = (c: PaymentAgingCell, status: StatusMode): number =>
  status === 'unsettled' ? c.unsettled : status === 'settled' ? c.settled : c.settled + c.unsettled;
const totalValue = (t: { unsettled: number; settled: number; due: number } | undefined, status: StatusMode): number =>
  !t ? 0 : status === 'unsettled' ? t.unsettled : status === 'settled' ? t.settled : t.settled + t.unsettled;

// 支払いがある(=金額が0でない)取引先の数
const vendorCountForMonth = (vendors: PaymentAgingVendorRow[], mk: string, status: StatusMode): number =>
  vendors.reduce((n, v) => n + (cellValue(v.cells[mk] ?? EMPTY_CELL, status) !== 0 ? 1 : 0), 0);
const vendorCountForTotal = (vendors: PaymentAgingVendorRow[], status: StatusMode): number =>
  vendors.reduce((n, v) => n + (totalValue(v.row_total, status) !== 0 ? 1 : 0), 0);

// 金額の下に小さく件数(社)を出す
const CountTag: React.FC<{ n: number }> = ({ n }) => (
  <div style={{ fontSize: '0.625rem', fontWeight: 600, color: FAINT, lineHeight: 1.3 }}>{n}社</div>
);

// ===========================================================================
// 洗練パレット (ニュートラル + 単一アクセント)
// ===========================================================================
const INK = '#26231f';        // 主テキスト
const INK_SOFT = '#736d65';   // 副次テキスト
const FAINT = '#c2bdb5';      // 0/空 のダッシュ
const LINE = '#eeece9';       // 行 hairline
const RULE = '#ddd9d3';       // セクション罫
const HEAD_BG = '#fbfaf8';    // ヘッダ背景
const ACCENT = '#2f5fd0';     // アクセント藍 (選択 / 未払の強調)
const ACCENT_BG = '#eef3fc';  // アクセント極薄
const PAID = '#8a8f98';       // 実績(支払済)のニュートラル
const NEG = '#c0392b';        // マイナス
const ZEBRA = '#fcfbf9';

const numCss: React.CSSProperties = { fontVariantNumeric: 'tabular-nums', fontFeatureSettings: '"tnum"' };

// セルの色 (status とセル内訳から)
const amtColor = (c: PaymentAgingCell, status: StatusMode, value: number): string => {
  if (value < 0) return NEG;
  if (status === 'settled') return PAID;
  if (c.unsettled > 0) return ACCENT; // 未払が残る → 目を引く
  return INK;
};

const yearOf = (mk: string) => mk.slice(2, 4); // 'YY'

const S = {
  page: { background: 'linear-gradient(180deg, #f8f7f5 0%, #f1efec 100%)', minHeight: '100vh', paddingBottom: '4rem' } as React.CSSProperties,
  ctrlLabel: { fontSize: '0.625rem', color: INK_SOFT, fontWeight: 700, letterSpacing: '0.05em', marginBottom: '4px', textTransform: 'uppercase' as const } as React.CSSProperties,
  card: { } as React.CSSProperties,
  table: { borderCollapse: 'separate' as const, borderSpacing: 0, width: '100%', fontSize: '0.8125rem', tableLayout: 'fixed' as const } as React.CSSProperties,
};

// 共通の列幅 (サマリーと明細で揃えて視覚的に整列)
const COL_NAME = 248;
const COL_MONTH = 104;
const COL_TOTAL = 128;

const ColGroup: React.FC<{ months: string[] }> = ({ months }) => (
  <colgroup>
    <col style={{ width: COL_NAME }} />
    {months.map((mk) => <col key={mk} style={{ width: COL_MONTH }} />)}
    <col style={{ width: COL_TOTAL }} />
  </colgroup>
);

const MonthHead: React.FC<{ mk: string; label: string; latest: boolean; sticky?: boolean; onClick?: () => void; arrow?: string }> = ({ mk, label, latest, sticky, onClick, arrow }) => (
  <th onClick={onClick} style={{
    ...(sticky ? { position: 'sticky' as const, top: 0, zIndex: 2 } : {}),
    background: latest ? ACCENT_BG : HEAD_BG, borderBottom: `1px solid ${RULE}`,
    padding: '7px 12px', textAlign: 'right', whiteSpace: 'nowrap',
    cursor: onClick ? 'pointer' : undefined, userSelect: 'none',
  }}>
    <div style={{ fontSize: '0.8125rem', fontWeight: 700, color: latest ? ACCENT : INK, lineHeight: 1.1 }}>{label}{arrow ? <span style={{ color: ACCENT, fontSize: '0.7rem' }}> {arrow}</span> : null}</div>
    <div style={{ fontSize: '0.5625rem', color: latest ? '#7a9adf' : '#b4afa8', fontWeight: 600, letterSpacing: '0.03em' }}>’{yearOf(mk)}</div>
  </th>
);

// ===========================================================================
// 区分別サマリー (上部・常時表示・行クリックで選択)
// ===========================================================================
const SummaryBand: React.FC<{
  data: PaymentAgingResponse;
  status: StatusMode;
  selected: string;
  onSelect: (cat: string) => void;
  order: string[];
}> = ({ data, status, selected, onSelect, order }) => {
  const { month_keys, month_labels } = data.header;
  const lastIdx = month_keys.length - 1;
  const groupMap = new Map(data.categories.map((g) => [g.category, g]));
  const allVendors = data.categories.flatMap((g) => g.vendors);

  const thLabel: React.CSSProperties = {
    background: HEAD_BG, borderBottom: `1px solid ${RULE}`, padding: '7px 14px',
    textAlign: 'left', fontSize: '0.625rem', fontWeight: 700, color: INK_SOFT, letterSpacing: '0.05em',
  };
  const thTotal: React.CSSProperties = { ...thLabel, textAlign: 'right', borderLeft: `1px solid ${LINE}` };

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={S.table}>
        <ColGroup months={month_keys} />
        <thead>
          <tr>
            <th style={thLabel}>区分</th>
            {month_keys.map((mk, i) => <MonthHead key={mk} mk={mk} label={month_labels[i]} latest={i === lastIdx} />)}
            <th style={thTotal}>合計</th>
          </tr>
        </thead>
        <tbody>
          {order.map((cat) => {
            const g = groupMap.get(cat);
            const isSel = cat === selected;
            const rowBg = isSel ? ACCENT_BG : '#fff';
            const labelColor = isSel ? ACCENT : INK;
            const count = g ? g.vendors.length : 0;
            return (
              <tr key={cat} onClick={() => onSelect(cat)} style={{ cursor: 'pointer', background: rowBg }}>
                <td style={{
                  padding: '9px 14px', borderBottom: `1px solid ${LINE}`,
                  borderLeft: `3px solid ${isSel ? ACCENT : 'transparent'}`, whiteSpace: 'nowrap',
                }}>
                  <span style={{ fontWeight: 700, fontSize: '0.84rem', color: labelColor }}>{cat}</span>
                  <span style={{ marginLeft: 8, fontSize: '0.6875rem', color: INK_SOFT, fontWeight: 600 }}>{count}社</span>
                </td>
                {month_keys.map((mk, i) => {
                  const v = g ? totalValue(g.subtotal.by_month[mk], status) : 0;
                  const n = g ? vendorCountForMonth(g.vendors, mk, status) : 0;
                  const latest = i === lastIdx;
                  return (
                    <td key={mk} style={{
                      padding: '9px 12px', textAlign: 'right', borderBottom: `1px solid ${LINE}`,
                      background: latest && !isSel ? '#f6f8fd' : undefined, ...numCss,
                      color: v === 0 ? FAINT : INK, fontWeight: latest ? 700 : 500,
                    }}>
                      <div>{v === 0 ? '–' : yen(v)}</div>
                      {v !== 0 ? <CountTag n={n} /> : null}
                    </td>
                  );
                })}
                <td style={{
                  padding: '9px 14px', textAlign: 'right', borderBottom: `1px solid ${LINE}`,
                  borderLeft: `1px solid ${LINE}`, ...numCss, fontWeight: 700, color: labelColor,
                }}>
                  <div>{g ? yen(totalValue(g.subtotal.total, status)) : '–'}</div>
                  {g && totalValue(g.subtotal.total, status) !== 0 ? <CountTag n={vendorCountForTotal(g.vendors, status)} /> : null}
                </td>
              </tr>
            );
          })}
          {/* 合計 */}
          <tr style={{ background: '#faf9f7' }}>
            <td style={{ padding: '10px 14px', borderTop: `2px solid ${RULE}`, fontWeight: 800, color: INK, fontSize: '0.8125rem' }}>合計</td>
            {month_keys.map((mk, i) => {
              const v = totalValue(data.column_totals[mk], status);
              const n = vendorCountForMonth(allVendors, mk, status);
              return (
                <td key={mk} style={{ padding: '10px 12px', textAlign: 'right', borderTop: `2px solid ${RULE}`, ...numCss, fontWeight: 800, color: i === lastIdx ? ACCENT : INK }}>
                  <div>{v === 0 ? <span style={{ color: FAINT }}>–</span> : yen(v)}</div>
                  {v !== 0 ? <CountTag n={n} /> : null}
                </td>
              );
            })}
            <td style={{ padding: '10px 14px', textAlign: 'right', borderTop: `2px solid ${RULE}`, borderLeft: `1px solid ${LINE}`, ...numCss, fontWeight: 800, color: INK }}>
              <div>{yen(totalValue(data.grand_total, status))}</div>
              {totalValue(data.grand_total, status) !== 0 ? <CountTag n={vendorCountForTotal(allVendors, status)} /> : null}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

// ===========================================================================
// 区分セグメント (明細の切替)
// ===========================================================================
const CategorySegments: React.FC<{
  data: PaymentAgingResponse; selected: string; onSelect: (c: string) => void; order: string[];
}> = ({ data, selected, onSelect, order }) => {
  const groupMap = new Map(data.categories.map((g) => [g.category, g]));
  return (
    <div style={{ display: 'inline-flex', background: '#f1efec', borderRadius: 10, padding: 3, gap: 2 }}>
      {order.map((cat) => {
        const isSel = cat === selected;
        const count = groupMap.get(cat)?.vendors.length ?? 0;
        return (
          <button key={cat} type="button" onClick={() => onSelect(cat)} style={{
            border: 'none', cursor: 'pointer', borderRadius: 8, padding: '7px 16px',
            background: isSel ? '#ffffff' : 'transparent',
            boxShadow: isSel ? '0 1px 3px rgba(0,0,0,0.10)' : 'none',
            color: isSel ? ACCENT : INK_SOFT, fontWeight: isSel ? 800 : 600, fontSize: '0.8125rem',
            transition: 'all 0.12s', whiteSpace: 'nowrap',
          }}>
            {cat}
            <span style={{ marginLeft: 7, fontSize: '0.6875rem', fontWeight: 700, color: isSel ? ACCENT : '#aaa49c' }}>{count}</span>
          </button>
        );
      })}
    </div>
  );
};

// ===========================================================================
// 業者明細 (選択区分・金額降順・主要のみ)
// ===========================================================================
// チェック済みバッジ (セル右上・stale で警告色)
const CHECK_OK = '#1c7c3c';
const CHECK_STALE = '#c77700';
const CellCheckBadge: React.FC<{ cell: PaymentAgingCell }> = ({ cell }) => {
  const chk = cell.check;
  if (!chk?.checked) return null;
  const stale = chk.stale;
  const title = stale
    ? `チェック後に変動あり: ${chk.stale_reason || '取引が更新されています'}（チェック ${chk.checked_at || ''}）`
    : `チェック済み ${chk.checked_at || ''}`;
  return (
    <span title={title} style={{
      position: 'absolute', top: 1, right: 2, fontSize: '0.625rem', fontWeight: 800, lineHeight: 1,
      color: stale ? CHECK_STALE : CHECK_OK,
    }}>{stale ? '⚠' : '✓'}</span>
  );
};

const DetailTable: React.FC<{
  group: PaymentAgingCategoryGroup | null;
  monthKeys: string[]; monthLabels: string[]; status: StatusMode; majorOnly: boolean;
  onCellClick: (v: PaymentAgingVendorRow, mk: string, cell: PaymentAgingCell) => void;
  onToggleCheck: (v: PaymentAgingVendorRow, mk: string, cell: PaymentAgingCell) => void;
  expanded: boolean;
}> = ({ group, monthKeys, monthLabels, status, majorOnly, onCellClick, onToggleCheck, expanded }) => {
  const lastIdx = monthKeys.length - 1;
  const [sortKey, setSortKey] = useState<string>('__total__'); // '__total__' または monthKey
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const onSort = (key: string) => {
    if (sortKey === key) setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
    else { setSortKey(key); setSortDir('desc'); }
  };
  const arrowFor = (key: string) => (sortKey === key ? (sortDir === 'desc' ? '▼' : '▲') : '');

  const vendors = useMemo(() => {
    if (!group) return [];
    const rows = group.vendors.map((v) => {
      const total = totalValue(v.row_total, status);
      const active = monthKeys.reduce((n, mk) => n + (cellValue(v.cells[mk] ?? EMPTY_CELL, status) !== 0 ? 1 : 0), 0);
      return { v, total, active };
    });
    const filtered = majorOnly ? rows.filter((r) => r.active >= 2 || Math.abs(r.total) >= 100000) : rows;
    const visible = filtered.filter((r) => r.total !== 0 || !majorOnly);
    const sv = (r: { v: PaymentAgingVendorRow; total: number }) =>
      sortKey === '__total__' ? r.total : cellValue(r.v.cells[sortKey] ?? EMPTY_CELL, status);
    return visible.sort((a, b) => { const d = sv(a) - sv(b); return sortDir === 'desc' ? -d : d; });
  }, [group, monthKeys, status, majorOnly, sortKey, sortDir]);

  const thName: React.CSSProperties = {
    position: 'sticky', left: 0, top: 0, zIndex: 3, background: HEAD_BG,
    borderBottom: `1px solid ${RULE}`, borderRight: `1px solid ${LINE}`,
    padding: '8px 14px', textAlign: 'left', fontSize: '0.625rem', fontWeight: 700, color: INK_SOFT, letterSpacing: '0.05em', whiteSpace: 'nowrap',
  };
  const thTot: React.CSSProperties = {
    position: 'sticky', top: 0, zIndex: 2, background: HEAD_BG, borderBottom: `1px solid ${RULE}`,
    borderLeft: `1px solid ${LINE}`, padding: '8px 14px', textAlign: 'right', fontSize: '0.625rem', fontWeight: 700, color: INK_SOFT, letterSpacing: '0.05em',
  };

  if (!group || vendors.length === 0) {
    return (
      <div style={{ padding: '48px', textAlign: 'center', color: FAINT, fontSize: '0.8125rem' }}>
        {majorOnly ? '主要業者(複数月に出現)はいません。「全業者」に切り替えて確認できます。' : 'この区分に該当する業者はありません。'}
      </div>
    );
  }

  return (
    <div style={{ maxHeight: expanded ? 'none' : '74vh', overflow: 'auto' }} className="pa-detail-scroll">
      <table style={S.table}>
        <ColGroup months={monthKeys} />
        <thead>
          <tr>
            <th style={thName}>業者（{vendors.length}社）<span style={{ fontWeight: 600, color: FAINT }}>　月/合計クリックで並替</span></th>
            {monthKeys.map((mk, i) => <MonthHead key={mk} mk={mk} label={monthLabels[i]} latest={i === lastIdx} sticky onClick={() => onSort(mk)} arrow={arrowFor(mk)} />)}
            <th style={{ ...thTot, cursor: 'pointer', userSelect: 'none' }} onClick={() => onSort('__total__')}>合計{arrowFor('__total__') ? ` ${arrowFor('__total__')}` : ''}</th>
          </tr>
        </thead>
        <tbody>
          {vendors.map(({ v, total }, ri) => {
            const bg = ri % 2 === 0 ? '#ffffff' : ZEBRA;
            return (
              <tr key={v.partner_id} className="pa-row">
                <td style={{
                  position: 'sticky', left: 0, zIndex: 1, background: bg, borderRight: `1px solid ${LINE}`,
                  borderBottom: `1px solid ${LINE}`, padding: '7px 14px', whiteSpace: 'nowrap',
                  overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: COL_NAME,
                  fontWeight: 600, color: INK, fontSize: '0.8125rem',
                }} title={v.partner_name}>{v.partner_name}</td>
                {monthKeys.map((mk, i) => {
                  const c = v.cells[mk] ?? EMPTY_CELL;
                  const val = cellValue(c, status);
                  const latest = i === lastIdx;
                  const nDeals = c.deal_ids.length + c.settled_deal_ids.length;
                  const clickable = nDeals > 0;
                  const checked = !!c.check?.checked;
                  return (
                    <td key={mk} onClick={clickable ? () => onCellClick(v, mk, c) : undefined} style={{
                      position: 'relative',
                      padding: '7px 12px', textAlign: 'right', borderBottom: `1px solid ${LINE}`,
                      background: checked && c.check?.stale ? '#fdf3e4' : checked ? '#eef8f0' : latest ? (ri % 2 === 0 ? '#f6f8fd' : '#f1f5fc') : bg, ...numCss,
                      color: val === 0 ? FAINT : amtColor(c, status, val), fontWeight: val === 0 ? 400 : 600,
                      cursor: clickable ? 'pointer' : 'default',
                    }} title={clickable ? `取引明細を表示（${nDeals}件）` : undefined} className={clickable ? 'pa-cell-link' : undefined}>
                      <CellCheckBadge cell={c} />
                      {clickable && (
                        <button type="button" className="pa-check-btn"
                          onClick={(e) => { e.stopPropagation(); onToggleCheck(v, mk, c); }}
                          title={checked ? 'チェックを外す' : 'チェック済みにする'}
                          style={{
                            position: 'absolute', top: 0, left: 2, border: 'none', background: 'transparent',
                            cursor: 'pointer', fontSize: '0.6875rem', lineHeight: 1, padding: '2px',
                            color: checked ? CHECK_OK : '#b9b4ac', fontWeight: 800,
                          }}>{checked ? '☑' : '☐'}</button>
                      )}
                      {val === 0 ? '–' : yen(val)}
                    </td>
                  );
                })}
                <td style={{
                  padding: '7px 14px', textAlign: 'right', background: bg, borderLeft: `1px solid ${LINE}`,
                  borderBottom: `1px solid ${LINE}`, ...numCss, fontWeight: 700, color: INK,
                }}>{total === 0 ? <span style={{ color: FAINT }}>–</span> : yen(total)}</td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr>
            <td style={{ position: 'sticky', left: 0, bottom: 0, zIndex: 3, background: '#2a2723', color: '#fff', fontWeight: 700, padding: '9px 14px', borderRight: '1px solid #1c1a17', whiteSpace: 'nowrap', fontSize: '0.8125rem' }}>
              {group.category} 小計
            </td>
            {monthKeys.map((mk, i) => {
              const v = totalValue(group.subtotal.by_month[mk], status);
              return (
                <td key={mk} style={{ position: 'sticky', bottom: 0, zIndex: 2, background: i === lastIdx ? '#33506e' : '#2a2723', color: '#fff', padding: '9px 12px', textAlign: 'right', ...numCss, fontWeight: 700, borderLeft: '1px solid #ffffff14' }}>
                  {v === 0 ? <span style={{ color: '#ffffff55' }}>–</span> : yen(v)}
                </td>
              );
            })}
            <td style={{ position: 'sticky', bottom: 0, zIndex: 2, background: '#15110e', color: '#fff', padding: '9px 14px', textAlign: 'right', ...numCss, fontWeight: 800, borderLeft: '1px solid #1c1a17' }}>
              {yen(totalValue(group.subtotal.total, status))}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
};

// ===========================================================================
// 区分割当ドロワー (右スライド・洗練) — カテゴリは動的 (2026-07-22)
// ===========================================================================
const VendorAssignPanel: React.FC<{
  vendors: PaymentAgingVendor[] | null; loading: boolean; counts: Record<string, number>;
  cats: string[];
  savingPartnerId: number | null; bulkRunning: boolean;
  onAssign: (v: PaymentAgingVendor, category: string) => void;
  onClear: (v: PaymentAgingVendor) => void; onBulk: () => void; onClose: () => void;
}> = ({ vendors, loading, cats, savingPartnerId, bulkRunning, onAssign, onClear, onBulk, onClose }) => {
  const [tab, setTab] = useState<string>('未割当');
  const ASSIGN_TABS = useMemo(() => [...cats, '未割当'], [cats]);
  const filtered = useMemo(() => {
    if (!vendors) return [];
    if (tab === '未割当') return vendors.filter((v) => !v.assigned_category);
    return vendors.filter((v) => v.assigned_category === tab);
  }, [vendors, tab]);
  const catSelectOptions = useMemo(() => [{ name: '— 未割当 —', value: '' }, ...cats.map((c) => ({ name: c, value: c }))], [cats]);
  const unassignedCount = vendors ? vendors.filter((v) => !v.assigned_category).length : 0;

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(28,26,23,0.34)', zIndex: 1000 }} />
      <div style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: 'min(560px, 94vw)', background: '#faf9f7', zIndex: 1001, boxShadow: '-10px 0 30px rgba(0,0,0,0.15)', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '18px 22px', borderBottom: `1px solid ${RULE}`, background: '#fff' }}>
          <HStack gap={0.5} alignItems="center" justifyContent="space-between">
            <div>
              <div style={{ fontSize: '1.0625rem', fontWeight: 800, color: INK }}>業者を区分に割り当て</div>
              <div style={{ fontSize: '0.75rem', color: INK_SOFT, marginTop: '3px' }}>{cats.join(' / ')} に分類（変更は自動保存）</div>
            </div>
            <Button appearance="tertiary" onClick={onClose} IconComponent={MdClose}>閉じる</Button>
          </HStack>
          <div style={{ marginTop: '12px' }}>
            <Button appearance="primary" small IconComponent={MdAutoFixHigh} onClick={onBulk} disabled={bulkRunning || unassignedCount === 0}>
              {bulkRunning ? <InlineSpinner isLoading /> : null} 勘定科目から一括割当（未割当 {unassignedCount} 社）
            </Button>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 4, padding: '14px 22px 0', flexWrap: 'wrap' }}>
          {ASSIGN_TABS.map((t) => {
            const active = tab === t;
            const isUn = t === '未割当';
            // バッジは手動割当ベース(リストの絞り込み条件と一致)。買掛+未払+家賃+未割当=総数。
            const cnt = isUn ? unassignedCount : (vendors ? vendors.filter((v) => v.assigned_category === t).length : 0);
            const c = isUn ? NEG : ACCENT;
            return (
              <button key={t} type="button" onClick={() => setTab(t)} style={{
                cursor: 'pointer', padding: '7px 14px', borderRadius: 999, fontSize: '0.8125rem', fontWeight: 700,
                border: `1px solid ${active ? c : '#e2dfda'}`, background: active ? (isUn ? '#fdecec' : ACCENT_BG) : '#fff', color: active ? c : INK_SOFT,
              }}>
                {t}<span style={{ marginLeft: 6, fontSize: '0.6875rem', fontWeight: 800, color: active ? c : '#aaa49c' }}>{cnt}</span>
              </button>
            );
          })}
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '14px 22px 28px' }}>
          {loading ? (
            <div style={{ padding: '40px', textAlign: 'center', color: INK_SOFT }}><InlineSpinner isLoading /> 業者を読み込み中…</div>
          ) : filtered.length === 0 ? (
            <div style={{ padding: '40px', textAlign: 'center', color: FAINT }}>{tab === '未割当' ? '未割当の業者はありません' : 'この区分の業者はありません'}</div>
          ) : (
            <VStack gap={0.5}>
              {filtered.map((v) => {
                const saving = savingPartnerId === v.partner_id;
                return (
                  <div key={v.partner_id} style={{ background: '#fff', border: `1px solid ${LINE}`, borderRadius: 10, padding: '11px 14px', opacity: saving ? 0.55 : 1, transition: 'opacity 0.15s' }}>
                    <HStack gap={0.5} alignItems="center" justifyContent="space-between">
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ fontWeight: 700, color: INK, fontSize: '0.875rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={v.partner_name}>{v.partner_name}</div>
                        <div style={{ fontSize: '0.6875rem', color: INK_SOFT, marginTop: '3px', display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                          <span style={{ ...numCss, fontWeight: 700 }}>直近 {yen(v.total_amount) || '¥0'}</span>
                          <span style={{ color: ACCENT, fontWeight: 700 }}>提案: {v.suggested_category}{v.confidence > 0 ? ` ${Math.round(v.confidence * 100)}%` : ''}</span>
                          {v.top_account_name && <span style={{ color: '#aaa49c' }}>{v.top_account_name} {v.sample_count}件</span>}
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <SelectBox name={`cat-${v.partner_id}`} value={v.assigned_category ?? ''} options={catSelectOptions} width="small" disabled={saving}
                          onChange={(e: any) => { const next = e.target.value as string; if (next === '') onClear(v); else onAssign(v, next); }} />
                        {saving && <span style={{ fontSize: '0.75rem', color: ACCENT }}>…</span>}
                      </div>
                    </HStack>
                  </div>
                );
              })}
            </VStack>
          )}
        </div>
      </div>
    </>
  );
};

// ===========================================================================
// 区分管理モーダル (2026-07-22): 追加 / 削除 / 順番入替
// ===========================================================================
const CategoryManager: React.FC<{
  cats: PaymentAgingCategoryDef[]; onChanged: () => void; onClose: () => void;
  flash: (msg: string) => void; setError: (msg: string) => void;
}> = ({ cats, onChanged, onClose, flash, setError }) => {
  const [newLabel, setNewLabel] = useState('');
  const [busy, setBusy] = useState(false);

  const move = async (idx: number, dir: -1 | 1) => {
    const labels = cats.map((c) => c.label);
    const j = idx + dir;
    if (j < 0 || j >= labels.length) return;
    [labels[idx], labels[j]] = [labels[j], labels[idx]];
    setBusy(true);
    try { await reorderPaymentAgingCategories(labels); onChanged(); }
    catch (e: any) { setError(`並び替えに失敗: ${e?.message || ''}`); }
    finally { setBusy(false); }
  };
  const add = async () => {
    const label = newLabel.trim();
    if (!label) return;
    setBusy(true);
    try { await createPaymentAgingCategory(label); setNewLabel(''); flash(`区分「${label}」を追加しました`); onChanged(); }
    catch (e: any) { setError(`追加に失敗: ${e?.message || ''}`); }
    finally { setBusy(false); }
  };
  const remove = async (c: PaymentAgingCategoryDef) => {
    const msg = c.vendor_count > 0
      ? `区分「${c.label}」には ${c.vendor_count} 社が割り当てられています。削除すると割当がクリアされ、各業者は自動サジェストに戻ります。削除しますか?`
      : `区分「${c.label}」を削除しますか?`;
    if (!window.confirm(msg)) return;
    setBusy(true);
    try { await deletePaymentAgingCategory(c.label, c.vendor_count > 0); flash(`区分「${c.label}」を削除しました`); onChanged(); }
    catch (e: any) { setError(`削除に失敗: ${e?.message || ''}`); }
    finally { setBusy(false); }
  };

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(28,26,23,0.34)', zIndex: 1210 }} />
      <div style={{
        position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 1211,
        background: '#fff', borderRadius: 12, boxShadow: '0 16px 48px rgba(0,0,0,0.25)', width: 'min(460px, 92vw)',
        maxHeight: '80vh', display: 'flex', flexDirection: 'column',
      }}>
        <div style={{ padding: '16px 20px', borderBottom: `1px solid ${RULE}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: '1rem', fontWeight: 800, color: INK }}>区分の管理</div>
            <div style={{ fontSize: '0.6875rem', color: INK_SOFT, marginTop: 2 }}>区分の追加・削除・↑↓で表示順を変更（組込み3区分は削除不可）</div>
          </div>
          <Button appearance="tertiary" small onClick={onClose} IconComponent={MdClose}>閉じる</Button>
        </div>
        <div style={{ padding: '12px 20px', overflowY: 'auto', flex: 1 }}>
          <VStack gap={0.25}>
            {cats.map((c, i) => (
              <div key={c.label} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', background: '#faf9f7', borderRadius: 8, border: `1px solid ${LINE}`, opacity: busy ? 0.6 : 1 }}>
                <span style={{ color: FAINT, fontSize: '0.6875rem', fontWeight: 800, width: 16 }}>{i + 1}</span>
                <span style={{ flex: 1, fontWeight: 700, color: INK, fontSize: '0.875rem' }}>{c.label}</span>
                <span style={{ fontSize: '0.6875rem', color: INK_SOFT }}>{c.vendor_count}社</span>
                {c.builtin && <span style={{ fontSize: '0.625rem', color: '#aaa49c', border: '1px solid #e2dfda', borderRadius: 4, padding: '1px 5px' }}>組込</span>}
                <button type="button" disabled={busy || i === 0} onClick={() => move(i, -1)} style={{ cursor: i === 0 ? 'default' : 'pointer', border: `1px solid ${LINE}`, background: '#fff', borderRadius: 6, padding: '2px 7px', color: i === 0 ? FAINT : INK }}>↑</button>
                <button type="button" disabled={busy || i === cats.length - 1} onClick={() => move(i, 1)} style={{ cursor: i === cats.length - 1 ? 'default' : 'pointer', border: `1px solid ${LINE}`, background: '#fff', borderRadius: 6, padding: '2px 7px', color: i === cats.length - 1 ? FAINT : INK }}>↓</button>
                <button type="button" disabled={busy || c.builtin} onClick={() => remove(c)} title={c.builtin ? '組込み区分は削除できません' : '削除'}
                  style={{ cursor: c.builtin ? 'default' : 'pointer', border: 'none', background: 'transparent', color: c.builtin ? '#ddd8d1' : NEG, fontWeight: 800, fontSize: '0.875rem', padding: '2px 6px' }}>×</button>
              </div>
            ))}
          </VStack>
          <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
            <input value={newLabel} onChange={(e) => setNewLabel(e.target.value)} placeholder="新しい区分名 (例: リース料)"
              onKeyDown={(e) => { if (e.key === 'Enter') add(); }}
              style={{ flex: 1, border: `1px solid ${RULE}`, borderRadius: 8, padding: '8px 12px', fontSize: '0.8125rem' }} />
            <Button appearance="primary" small onClick={add} disabled={busy || !newLabel.trim()}>追加</Button>
          </div>
        </div>
      </div>
    </>
  );
};

// ===========================================================================
// 取引明細モーダル (2026-07-22): セルクリック → freee 取引を複式簿記形式で表示
//   + 証憑プレビュー (worker OAuth proxy) + 修正指示送信 (#journal_review 経路)
// ===========================================================================
const DealModal: React.FC<{
  title: string; dealIds: number[]; onClose: () => void; flash: (msg: string) => void;
}> = ({ title, dealIds, onClose, flash }) => {
  const [items, setItems] = useState<Array<{ deal: PaymentAgingDeal; group: JournalGroupData }> | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [corrOpen, setCorrOpen] = useState<Record<number, boolean>>({});
  const [corrText, setCorrText] = useState<Record<number, string>>({});
  const [corrLearn, setCorrLearn] = useState<Record<number, boolean>>({});
  const [corrBusy, setCorrBusy] = useState<Record<number, boolean>>({});
  const [corrDone, setCorrDone] = useState<Record<number, string>>({});
  const [receiptView, setReceiptView] = useState<{ id: number; mime: string } | null>(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        // 取引 + マスタ名称 map (module cache 済) を並列取得 → 仕訳レビューと同一の変換で複式化
        const [r, accounts, sections, taxCodes, itemsMap, tags, partnersArr, wallets] = await Promise.all([
          fetchPaymentAgingCell(dealIds),
          fetchAccountItemsMap(), fetchSectionsMap(), fetchTaxCodesMap(),
          fetchItemsMap(), fetchTagsMap(), fetchAllFreeePartners(), fetchWalletablesMap(),
        ]);
        if (!alive) return;
        const partners = new Map<number, string>();
        for (const p of partnersArr) partners.set(Number(p.id), p.name);
        const maps: NameMaps = { accounts, sections, taxCodes, items: itemsMap, tags, partners, wallets };
        setItems(r.deals.map((d) => ({ deal: d, group: dealToJournalGroup(d as unknown as JournalReviewDeal, maps, { detailFetched: true }) })));
        if (r.errors && r.errors.length) setLoadErr(`${r.errors.length} 件の取引取得に失敗`);
      } catch (e: any) {
        if (alive) setLoadErr(e?.message || '取引の取得に失敗しました');
      }
    })();
    return () => { alive = false; };
  }, [dealIds]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { if (receiptView) setReceiptView(null); else onClose(); } };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, receiptView]);

  const sendCorrection = async (dealId: number) => {
    const text = (corrText[dealId] || '').trim();
    if (!text || corrBusy[dealId]) return;
    const learn = corrLearn[dealId] !== false;
    setCorrBusy((s) => ({ ...s, [dealId]: true }));
    try {
      // 2026-07-22: companion job は worker corrections POST が dedupe 付きで自動発行
      // (吉田商店 #33 取りこぼし恒久修正)。client 側 createJob は廃止。
      const r = await createJournalCorrection({ deal_id: dealId, instruction_text: text, created_by: 'web_app_user', learn });
      if (r?.job?.error) throw new Error(`指示は保存されたが配送失敗: ${r.job.error}`);
      setCorrDone((s) => ({ ...s, [dealId]: `送信済み (#${r.id ?? '—'})` }));
      setCorrText((s) => ({ ...s, [dealId]: '' }));
      flash('修正指示を送信しました（担当AIが処理し、#journal_review で状態確認できます）');
    } catch (e: any) {
      setCorrDone((s) => ({ ...s, [dealId]: `送信失敗: ${e?.message || ''}` }));
    } finally {
      setCorrBusy((s) => ({ ...s, [dealId]: false }));
    }
  };

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(28,26,23,0.4)', zIndex: 1220 }} />
      <div style={{
        position: 'fixed', top: '4vh', left: '50%', transform: 'translateX(-50%)', zIndex: 1221,
        background: '#fff', borderRadius: 12, boxShadow: '0 18px 56px rgba(0,0,0,0.3)', width: 'min(1080px, 96vw)',
        maxHeight: '92vh', display: 'flex', flexDirection: 'column',
      }}>
        <style>{DEAL_LEDGER_CSS}</style>
        <div style={{ padding: '14px 20px', borderBottom: `1px solid ${RULE}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
          <div style={{ fontSize: '0.9375rem', fontWeight: 800, color: INK }}>{title}</div>
          <Button appearance="tertiary" small onClick={onClose} IconComponent={MdClose}>閉じる</Button>
        </div>
        <div style={{ overflowY: 'auto', padding: '14px 20px 20px', flex: 1 }}>
          {loadErr && <MessageBlock success={false}>{loadErr}</MessageBlock>}
          {!items && !loadErr && (
            <div style={{ padding: 40, textAlign: 'center', color: INK_SOFT }}><InlineSpinner isLoading /> freee から取引を取得中…</div>
          )}
          {items && items.map(({ deal: d, group }) => {
            const isUnsettled = d.status === 'unsettled';
            return (
              <div key={d.id} style={{ border: `1px solid ${LINE}`, borderRadius: 10, marginBottom: 14, overflow: 'hidden' }}>
                <div style={{ padding: '10px 14px', background: '#faf9f7', display: 'flex', gap: 14, alignItems: 'baseline', flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: 800, color: INK }}>{group.title}</span>
                  <span style={{ fontWeight: 800, color: INK }}>{d.issue_date}</span>
                  <span style={{ fontWeight: 800, color: ACCENT, ...numCss }}>{yen(d.amount)}</span>
                  <span style={{ fontSize: '0.6875rem', fontWeight: 700, color: isUnsettled ? ACCENT : PAID, border: `1px solid ${isUnsettled ? ACCENT : '#d5d8de'}`, borderRadius: 999, padding: '1px 8px' }}>
                    {isUnsettled ? '未決済（支払予定）' : '決済済'}
                  </span>
                  {d.due_date && <span style={{ fontSize: '0.6875rem', color: INK_SOFT }}>期日 {d.due_date}</span>}
                  <span style={{ flex: 1 }} />
                  <a href={freeeDealUrl(d.id)} target="_blank" rel="noopener noreferrer" style={{ fontSize: '0.75rem', color: ACCENT, fontWeight: 700 }}>freee で開く #{d.id}</a>
                </div>
                {/* 複式簿記形式 (仕訳レビューと同一の ledger 表示) */}
                <div style={{ padding: '10px 14px' }}>
                  <DealLedger group={group} />
                </div>
                {/* 証憑 */}
                {(d.receipts || []).length > 0 && (
                  <div style={{ padding: '10px 14px', borderTop: `1px solid ${LINE}` }}>
                    <div style={{ fontSize: '0.6875rem', fontWeight: 700, color: INK_SOFT, marginBottom: 6 }}>証憑（{(d.receipts || []).length}件）</div>
                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                      {(d.receipts || []).map((rc) => {
                        const mime = rc.mime_type || '';
                        const isImg = mime.startsWith('image/');
                        return (
                          <button key={rc.id} type="button" onClick={() => setReceiptView({ id: rc.id, mime })} style={{ cursor: 'zoom-in', border: `1px solid ${LINE}`, borderRadius: 8, background: '#fff', padding: 4, width: 108 }}>
                            {isImg
                              ? <img src={paymentAgingReceiptUrl(rc.id)} alt={`証憑 ${rc.id}`} style={{ width: '100%', height: 84, objectFit: 'cover', borderRadius: 5 }} loading="lazy" />
                              : <div style={{ width: '100%', height: 84, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f4f2ef', borderRadius: 5, fontWeight: 800, color: INK_SOFT, fontSize: '0.75rem' }}>PDF</div>}
                            <div style={{ fontSize: '0.625rem', color: INK_SOFT, marginTop: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {rc.receipt_metadatum?.partner_name || `#${rc.id}`}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
                {/* 修正指示 */}
                <div style={{ padding: '10px 14px', borderTop: `1px solid ${LINE}`, background: '#fdfdfc' }}>
                  {!corrOpen[d.id] ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <Button appearance="secondary" small onClick={() => setCorrOpen((s) => ({ ...s, [d.id]: true }))}>この取引に修正指示を送る</Button>
                      {corrDone[d.id] && <span style={{ fontSize: '0.75rem', color: corrDone[d.id].startsWith('送信済み') ? CHECK_OK : NEG, fontWeight: 700 }}>{corrDone[d.id]}</span>}
                    </div>
                  ) : (
                    <div>
                      <textarea value={corrText[d.id] || ''} onChange={(e) => setCorrText((s) => ({ ...s, [d.id]: e.target.value }))}
                        placeholder="修正内容を日本語で（例: 部門を本部から◯◯に変更 / 勘定科目を◯◯に変更）"
                        style={{ width: '100%', minHeight: 64, border: `1px solid ${RULE}`, borderRadius: 8, padding: '8px 12px', fontSize: '0.8125rem', resize: 'vertical', boxSizing: 'border-box' }} />
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 8, flexWrap: 'wrap' }}>
                        <label style={{ fontSize: '0.75rem', color: INK_SOFT, display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer' }}>
                          <input type="checkbox" checked={corrLearn[d.id] !== false} onChange={(e) => setCorrLearn((s) => ({ ...s, [d.id]: e.target.checked }))} />
                          今後のルールとして学習する
                        </label>
                        <Button appearance="primary" small onClick={() => sendCorrection(d.id)} disabled={corrBusy[d.id] || !(corrText[d.id] || '').trim()}>
                          {corrBusy[d.id] ? <InlineSpinner isLoading /> : null} 送信
                        </Button>
                        <Button appearance="tertiary" small onClick={() => setCorrOpen((s) => ({ ...s, [d.id]: false }))}>閉じる</Button>
                        {corrDone[d.id] && <span style={{ fontSize: '0.75rem', color: corrDone[d.id].startsWith('送信済み') ? CHECK_OK : NEG, fontWeight: 700 }}>{corrDone[d.id]}</span>}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {/* 証憑拡大ビュー */}
      {receiptView && (
        <>
          <div onClick={() => setReceiptView(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(15,14,12,0.72)', zIndex: 1230 }} />
          <div style={{ position: 'fixed', top: '3vh', left: '50%', transform: 'translateX(-50%)', zIndex: 1231, width: 'min(820px, 94vw)', height: '90vh', background: '#fff', borderRadius: 10, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '8px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: `1px solid ${RULE}` }}>
              <span style={{ fontSize: '0.8125rem', fontWeight: 700, color: INK }}>証憑 #{receiptView.id}</span>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                <a href={paymentAgingReceiptUrl(receiptView.id)} target="_blank" rel="noopener noreferrer" style={{ fontSize: '0.75rem', color: ACCENT, fontWeight: 700 }}>別タブで開く</a>
                <Button appearance="tertiary" small onClick={() => setReceiptView(null)} IconComponent={MdClose}>閉じる</Button>
              </div>
            </div>
            {receiptView.mime.startsWith('image/')
              ? <div style={{ flex: 1, overflow: 'auto', background: '#26231f', display: 'flex', alignItems: 'flex-start', justifyContent: 'center' }}><img src={paymentAgingReceiptUrl(receiptView.id)} alt="証憑" style={{ maxWidth: '100%' }} /></div>
              : <iframe title="証憑PDF" src={paymentAgingReceiptUrl(receiptView.id)} style={{ flex: 1, border: 'none' }} />}
          </div>
        </>
      )}
    </>
  );
};

// ===========================================================================
// メインページ
// ===========================================================================
// ===========================================================================
// 印刷専用: 完全レポート (A4横・全区分・全業者・白黒最適化・改ページ制御)
//   画面では非表示(.pa-print { display:none })、@media print でのみ表示。
// ===========================================================================
const PrintReport: React.FC<{ data: PaymentAgingResponse; status: StatusMode; order: string[] }> = ({ data, status, order }) => {
  const { month_keys, month_labels } = data.header;
  const groupMap = new Map(data.categories.map((g) => [g.category, g]));
  const ym = (k: string) => `${k.slice(0, 4)}年${Number(k.slice(5, 7))}月`;
  const period = month_keys.length ? `${ym(month_keys[0])}〜${ym(month_keys[month_keys.length - 1])}` : '';
  const gen = data.header.generated_at ? new Date(data.header.generated_at).toLocaleString('ja-JP') : '';
  const pv = (n: number) => (n === 0 ? <span className="pa-zero">–</span> : yen(n));
  const colGroup = (
    <colgroup><col style={{ width: '22%' }} />{month_keys.map((k) => <col key={k} />)}<col style={{ width: '11%' }} /></colgroup>
  );
  const headRow = (firstLabel: string) => (
    <tr><th className="name">{firstLabel}</th>{month_keys.map((k, i) => <th key={k} className="num">{month_labels[i]}<br />’{k.slice(2, 4)}</th>)}<th className="num">合計</th></tr>
  );

  return (
    <div className="pa-print">
      <div className="pa-ph">
        <div className="pa-ph-title">支払突合表</div>
        <div className="pa-ph-meta">クマガイコーポレーション株式会社　／　対象期間 {period}{data.header.pay_day === '25' ? '（決済期日・25日払い・税込）' : '（決済期日・全支払日・税込）'}</div>
        <div className="pa-ph-sub">集計 {gen}　／　✓=チェック済み　⚠=チェック後に取引の変動あり</div>
      </div>

      <div className="pa-sec">
        <h2>区分別サマリー</h2>
        <table className="pa-pt">
          {colGroup}
          <thead>{headRow('区分')}</thead>
          <tbody>
            {order.map((cat) => {
              const g = groupMap.get(cat);
              return (
                <tr key={cat} className="catrow">
                  <td className="name">{cat}（{g ? g.vendors.length : 0}社）</td>
                  {month_keys.map((k) => <td key={k} className="num">{pv(g ? totalValue(g.subtotal.by_month[k], status) : 0)}</td>)}
                  <td className="num">{pv(g ? totalValue(g.subtotal.total, status) : 0)}</td>
                </tr>
              );
            })}
            <tr className="totrow">
              <td className="name">合計</td>
              {month_keys.map((k) => <td key={k} className="num">{pv(totalValue(data.column_totals[k], status))}</td>)}
              <td className="num">{pv(totalValue(data.grand_total, status))}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {order.map((cat) => {
        const g = groupMap.get(cat);
        if (!g || g.vendors.length === 0) return null;
        const vendors = [...g.vendors].sort((a, b) => totalValue(b.row_total, status) - totalValue(a.row_total, status));
        return (
          <div className="pa-sec" key={cat}>
            <h2>■ {cat}（{vendors.length}社）　区分計 {yen(totalValue(g.subtotal.total, status))}</h2>
            <table className="pa-pt">
              {colGroup}
              <thead>{headRow('業者')}</thead>
              <tbody>
                {vendors.map((v) => (
                  <tr key={v.partner_id}>
                    <td className="name" title={v.partner_name}>{v.partner_name}</td>
                    {month_keys.map((k) => {
                      const c = v.cells[k] ?? EMPTY_CELL;
                      const chk = c.check;
                      return (
                        <td key={k} className="num">
                          {chk?.checked && <span className={chk.stale ? 'pa-chk-stale' : 'pa-chk'}>{chk.stale ? '⚠' : '✓'}</span>}
                          {pv(cellValue(c, status))}
                        </td>
                      );
                    })}
                    <td className="num">{pv(totalValue(v.row_total, status))}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="sub">
                  <td className="name">小計</td>
                  {month_keys.map((k) => <td key={k} className="num">{pv(totalValue(g.subtotal.by_month[k], status))}</td>)}
                  <td className="num">{pv(totalValue(g.subtotal.total, status))}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        );
      })}

      <div className="pa-grand">総合計　{yen(totalValue(data.grand_total, status))}</div>
    </div>
  );
};

export const PaymentAgingPage: React.FC = () => {
  const prefs = useMemo(() => loadPrefs(), []);

  const [endMonth, setEndMonth] = useState<string>(currentYm());
  const [months, setMonths] = useState<number>(prefs.months ?? 6);
  const [statusMode, setStatusMode] = useState<StatusMode>(prefs.statusMode ?? 'both');
  const [majorOnly, setMajorOnly] = useState<boolean>(prefs.majorOnly ?? true);
  const [payDay, setPayDay] = useState<PayDay>(prefs.payDay ?? '25');
  const [detailExpanded, setDetailExpanded] = useState(false);

  const [data, setData] = useState<PaymentAgingResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<string>('買掛金');

  const [assignOpen, setAssignOpen] = useState(false);
  const [vendors, setVendors] = useState<PaymentAgingVendor[] | null>(null);
  const [vendorCounts, setVendorCounts] = useState<Record<string, number>>({});
  const [vendorsLoading, setVendorsLoading] = useState(false);
  const [savingPartnerId, setSavingPartnerId] = useState<number | null>(null);
  const [bulkRunning, setBulkRunning] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // 2026-07-22 拡張: 動的カテゴリ / カテゴリ管理 / 取引明細モーダル
  const [catDefs, setCatDefs] = useState<PaymentAgingCategoryDef[]>([]);
  const [catMgrOpen, setCatMgrOpen] = useState(false);
  const [dealModal, setDealModal] = useState<{ title: string; dealIds: number[] } | null>(null);

  const loadCategories = useCallback(async () => {
    try { const r = await fetchPaymentAgingCategories(); setCatDefs(r.categories || []); } catch { /* fallback: 組込3区分 */ }
  }, []);
  useEffect(() => { loadCategories(); }, [loadCategories]);
  const dynamicCats = useMemo(() => catDefs.length ? catDefs.map((c) => c.label) : [...PAYMENT_CATEGORIES], [catDefs]);

  // 表示する区分の順序 (server が動的カテゴリ順で返す。未分類は末尾)
  const order = useMemo(() => {
    if (!data) return dynamicCats;
    const all = data.categories.map((g) => g.category);
    return all.length ? all : dynamicCats;
  }, [data, dynamicCats]);

  const monthsOptions = useMemo(() => MONTH_CHOICES.map((n) => ({ name: `${n}か月`, value: String(n) })), []);
  const endMonthOptions = useMemo(() => {
    const now = new Date();
    const arr: Array<{ name: string; value: string }> = [];
    for (let i = -17; i <= 1; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
      arr.push({ name: `${d.getFullYear()}年${d.getMonth() + 1}月`, value: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}` });
    }
    return arr.reverse();
  }, []);
  const statusOptions = useMemo(() => ([
    { name: '予定＋実績', value: 'both' },
    { name: '支払予定のみ', value: 'unsettled' },
    { name: '支払実績のみ', value: 'settled' },
  ]), []);
  const majorOptions = useMemo(() => ([{ name: '主要業者のみ', value: '1' }, { name: '全業者', value: '0' }]), []);
  const payDayOptions = useMemo(() => ([{ name: '25日締めのみ', value: '25' }, { name: 'すべての支払日', value: 'all' }]), []);

  useEffect(() => { savePrefs({ months, statusMode, majorOnly, payDay }); }, [months, statusMode, majorOnly, payDay]);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await fetchPaymentAgingMatrix(endMonth, months, statusMode, payDay);
      setData(res);
    } catch (err: any) {
      setError(err?.message || '集計の取得に失敗しました');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [endMonth, months, statusMode, payDay]);

  useEffect(() => { load(); }, [load]);

  // data 更新時、選択区分が消えたら最大の区分へ
  useEffect(() => {
    if (!data) return;
    const present = data.categories;
    if (present.some((g) => g.category === selected)) return;
    const top = [...present].sort((a, b) => totalValue(b.subtotal.total, statusMode) - totalValue(a.subtotal.total, statusMode))[0];
    if (top) setSelected(top.category);
  }, [data, selected, statusMode]);

  const flashToast = useCallback((msg: string) => {
    setToast(msg);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 2500);
  }, []);
  useEffect(() => () => { if (toastTimer.current) clearTimeout(toastTimer.current); }, []);

  const loadVendors = useCallback(async () => {
    setVendorsLoading(true);
    try {
      const res = await fetchPaymentAgingVendors(endMonth, months, payDay);
      setVendors(res.vendors); setVendorCounts(res.counts || {});
    } catch (err: any) {
      setError(err?.message || '業者一覧の取得に失敗しました');
    } finally {
      setVendorsLoading(false);
    }
  }, [endMonth, months, payDay]);

  const openAssign = useCallback(() => { setAssignOpen(true); loadVendors(); }, [loadVendors]);
  const closeAssign = useCallback(() => { setAssignOpen(false); load(); }, [load]);

  useEffect(() => {
    if (!assignOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') closeAssign(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [assignOpen, closeAssign]);

  const patchVendor = useCallback((pid: number, patch: Partial<PaymentAgingVendor>) => {
    setVendors((prev) => prev ? prev.map((v) => v.partner_id === pid ? { ...v, ...patch } : v) : prev);
  }, []);

  const handleAssign = useCallback(async (v: PaymentAgingVendor, category: string) => {
    const pc = v.assigned_category, ps = v.source;
    setSavingPartnerId(v.partner_id);
    patchVendor(v.partner_id, { assigned_category: category, source: 'user_input' });
    try {
      await savePaymentVendorCategory({ partner_id: v.partner_id, partner_name: v.partner_name, category, source: 'user_input' });
      flashToast(`「${v.partner_name}」を ${category} に割り当てました`);
    } catch (err: any) {
      patchVendor(v.partner_id, { assigned_category: pc, source: ps });
      setError(`保存に失敗: ${err?.message || ''}`);
    } finally { setSavingPartnerId(null); }
  }, [patchVendor, flashToast]);

  const handleClear = useCallback(async (v: PaymentAgingVendor) => {
    const pc = v.assigned_category, ps = v.source;
    setSavingPartnerId(v.partner_id);
    patchVendor(v.partner_id, { assigned_category: null, source: null });
    try {
      await clearPaymentVendorCategory(v.partner_id);
      flashToast(`「${v.partner_name}」の割当をクリアしました`);
    } catch (err: any) {
      patchVendor(v.partner_id, { assigned_category: pc, source: ps });
      setError(`クリアに失敗: ${err?.message || ''}`);
    } finally { setSavingPartnerId(null); }
  }, [patchVendor, flashToast]);

  const handleBulk = useCallback(async () => {
    if (!vendors) return;
    const targets = vendors
      .filter((v) => !v.assigned_category && v.confidence >= 0.5 && dynamicCats.includes(v.suggested_category))
      .map((v) => ({ partner_id: v.partner_id, category: v.suggested_category, source: 'auto_default', confidence: v.confidence, partner_name: v.partner_name }));
    if (targets.length === 0) { flashToast('一括割当できる(確度50%以上の)未割当業者がありません'); return; }
    setBulkRunning(true);
    try {
      const res = await bulkSavePaymentVendorCategory(targets);
      flashToast(`${res.applied} 社を勘定科目から一括割当しました`);
      await loadVendors();
    } catch (err: any) {
      setError(`一括割当に失敗: ${err?.message || ''}`);
    } finally { setBulkRunning(false); }
  }, [vendors, flashToast, loadVendors, dynamicCats]);

  // セルクリック → 複式簿記モーダル (2026-07-22。旧: freee 直リンク/popover)
  const onCellClick = useCallback((v: PaymentAgingVendorRow, mk: string, c: PaymentAgingCell) => {
    const ids = [...c.deal_ids, ...c.settled_deal_ids];
    if (ids.length === 0) return;
    const ym = `${mk.slice(0, 4)}年${Number(mk.slice(5, 7))}月`;
    setDealModal({ title: `${v.partner_name} — ${ym}（${ids.length}件）`, dealIds: ids });
  }, []);

  // チェック済みトグル (2026-07-22): snapshot はセルの表示値をそのまま保存 → 変動検知の基準
  const onToggleCheck = useCallback(async (v: PaymentAgingVendorRow, mk: string, c: PaymentAgingCell) => {
    const next = !c.check?.checked;
    try {
      await setPaymentAgingCheck({
        partner_id: v.partner_id, month: mk, pay_day: payDay, checked: next,
        snapshot: next ? { unsettled: c.unsettled, settled: c.settled, deal_ids: c.deal_ids, settled_deal_ids: c.settled_deal_ids } : undefined,
      });
      // 楽観更新 (再取得なしで即時反映 — freee 再集計は重いので避ける)
      setData((prev) => {
        if (!prev) return prev;
        const clone = { ...prev, categories: prev.categories.map((g) => ({
          ...g,
          vendors: g.vendors.map((vr) => vr.partner_id !== v.partner_id ? vr : ({
            ...vr,
            cells: { ...vr.cells, [mk]: { ...(vr.cells[mk] ?? EMPTY_CELL),
              check: next ? { checked: true, checked_by: 'web_app_user', checked_at: new Date().toISOString().slice(0, 19).replace('T', ' '), stale: false, stale_reason: null } : undefined } },
          })),
        })) };
        return clone;
      });
      flashToast(next ? `${v.partner_name} ${Number(mk.slice(5, 7))}月をチェック済みにしました` : `${v.partner_name} ${Number(mk.slice(5, 7))}月のチェックを外しました`);
    } catch (err: any) {
      setError(`チェックの保存に失敗: ${err?.message || ''}`);
    }
  }, [payDay, flashToast]);

  const selectedGroup = data?.categories.find((g) => g.category === selected) ?? null;
  const gt = data?.grand_total;
  const complexCount = data?.warnings?.length ?? 0;

  return (
    <div style={S.page}>
      <style>{`
        .pa-row:hover td { background: #f3f7fe !important; }
        .pa-cell-link:hover { text-decoration: underline; text-underline-offset: 2px; }
        .pa-deal-link:hover { background: #f3f7fe; }
        .pa-check-btn { opacity: 0; transition: opacity 0.1s; }
        .pa-row:hover .pa-check-btn { opacity: 1; }
        .pa-detail-scroll::-webkit-scrollbar { height: 10px; width: 10px; }
        .pa-detail-scroll::-webkit-scrollbar-thumb { background: #d8d4ce; border-radius: 6px; }
        .pa-print { display: none; }
        @media print {
          @page { size: A4 landscape; margin: 9mm 8mm; }
          html, body { background: #fff !important; }
          .pa-screen { display: none !important; }
          .pa-no-print { display: none !important; }
          .pa-print { display: block !important; color: #000; }
          .pa-print * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .pa-ph-title { font-size: 15pt; font-weight: 800; margin: 0; }
          .pa-ph-meta { font-size: 8.5pt; margin-top: 2pt; }
          .pa-ph-sub { font-size: 8pt; color: #333; }
          .pa-pt { width: 100%; border-collapse: collapse; font-size: 8pt; margin-top: 3pt; table-layout: fixed; }
          .pa-pt th, .pa-pt td { border: 0.4pt solid #999; padding: 1.6pt 4pt; overflow: hidden; }
          .pa-pt thead { display: table-header-group; }
          .pa-pt thead th { background: #eaeaea !important; font-weight: 700; font-size: 7.5pt; line-height: 1.05; }
          .pa-pt tbody tr { page-break-inside: avoid; }
          .pa-pt .num { text-align: right; font-variant-numeric: tabular-nums; white-space: nowrap; }
          .pa-pt .name { text-align: left; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
          .pa-pt .catrow td { font-weight: 700; }
          .pa-pt .sub td { font-weight: 800; border-top: 1pt solid #555; background: #f2f2f2 !important; }
          .pa-pt .totrow td { font-weight: 800; border-top: 1.2pt solid #000; background: #e6e6e6 !important; }
          .pa-sec { margin-top: 7pt; }
          .pa-sec h2 { font-size: 10pt; margin: 6pt 0 1pt; page-break-after: avoid; border-bottom: 1pt solid #000; padding-bottom: 1pt; }
          .pa-grand { margin-top: 7pt; text-align: right; font-size: 11pt; font-weight: 800; border-top: 1.5pt solid #000; padding-top: 3pt; }
          .pa-zero { color: #bbb; }
          .pa-chk { font-weight: 800; margin-right: 2pt; }
          .pa-chk-stale { font-weight: 800; margin-right: 2pt; }
        }
      `}</style>
      <div className="pa-screen">
      <Container>
        <div style={{ paddingTop: '18px' }}>
          <Breadcrumbs links={[{ title: 'FREEEMAN', url: '/' }, { title: '支払突合表' }]} />
        </div>
        <div style={{ marginTop: '4px', display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
          <PageTitle>支払突合表</PageTitle>
          <span style={{ fontSize: '0.75rem', color: INK_SOFT }}>freee の取引を決済期日で集計（{payDay === '25' ? '25日払いのみ・' : '全支払日・'}過去{months}か月・税込・キャッシュなし）</span>
        </div>

        {/* 操作バー */}
        <div style={{ marginTop: '12px' }} className="pa-no-print">
          <CardBase>
            <div style={{ padding: '14px 18px', display: 'flex', flexWrap: 'wrap', gap: '14px', alignItems: 'flex-end' }}>
              <div>
                <div style={S.ctrlLabel}>基準月（右端）</div>
                <SelectBox name="endMonth" value={endMonth} options={endMonthOptions} width="small" onChange={(e: any) => setEndMonth(e.target.value)} />
              </div>
              <div>
                <div style={S.ctrlLabel}>表示月数</div>
                <SelectBox name="months" value={String(months)} options={monthsOptions} width="small" onChange={(e: any) => setMonths(Number(e.target.value))} />
              </div>
              <div>
                <div style={S.ctrlLabel}>支払日</div>
                <SelectBox name="payday" value={payDay} options={payDayOptions} width="medium" onChange={(e: any) => setPayDay(e.target.value as PayDay)} />
              </div>
              <div>
                <div style={S.ctrlLabel}>金額</div>
                <SelectBox name="status" value={statusMode} options={statusOptions} width="small" onChange={(e: any) => setStatusMode(e.target.value as StatusMode)} />
              </div>
              <div>
                <div style={S.ctrlLabel}>業者</div>
                <SelectBox name="major" value={majorOnly ? '1' : '0'} options={majorOptions} width="small" onChange={(e: any) => setMajorOnly(e.target.value === '1')} />
              </div>
              <div style={{ flex: 1 }} />
              <Button onClick={load} disabled={loading} IconComponent={MdRefresh}>{loading ? <InlineSpinner isLoading /> : null} 再取得</Button>
              <Button appearance="secondary" onClick={openAssign} IconComponent={MdEdit}>区分を割り当て</Button>
              <Button appearance="secondary" onClick={() => setCatMgrOpen(true)}>区分の管理</Button>
              <Button appearance="tertiary" onClick={() => window.print()} IconComponent={MdPrint}>印刷</Button>
            </div>
          </CardBase>
        </div>

        {error && <div style={{ marginTop: '12px' }} className="pa-no-print"><MessageBlock success={false}>{error}</MessageBlock></div>}

        {/* 初回ローディング */}
        {loading && !data && (
          <div style={{ marginTop: '14px' }}>
            <CardBase>
              <div style={{ padding: '64px', textAlign: 'center', color: INK_SOFT }}>
                <InlineSpinner isLoading />
                <div style={{ marginTop: 14, fontSize: '0.875rem', fontWeight: 600 }}>freee から集計しています…</div>
                <div style={{ marginTop: 4, fontSize: '0.75rem', color: FAINT }}>決済期日の取引を直接集計するため数秒かかります</div>
              </div>
            </CardBase>
          </div>
        )}

        {data && (
          <>
            {/* サマリー */}
            <div style={{ marginTop: '14px', position: 'relative' }}>
              <CardBase>
                <div style={{ padding: '12px 18px 0', display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                  <div style={{ fontSize: '0.8125rem', fontWeight: 800, color: INK }}>区分別サマリー<span style={{ marginLeft: 8, fontSize: '0.6875rem', fontWeight: 600, color: FAINT }}>区分行クリックで明細を切替</span></div>
                  <div style={{ display: 'flex', gap: 18, fontSize: '0.75rem', ...numCss }}>
                    <span style={{ color: INK_SOFT }}>支払予定 <b style={{ color: ACCENT }}>{yen(gt?.unsettled) || '¥0'}</b></span>
                    <span style={{ color: INK_SOFT }}>支払実績 <b style={{ color: PAID }}>{yen(gt?.settled) || '¥0'}</b></span>
                  </div>
                </div>
                <div style={{ padding: '8px 6px 4px' }}>
                  <SummaryBand data={data} status={statusMode} selected={selected} onSelect={setSelected} order={order} />
                </div>
              </CardBase>
              {loading && (
                <div style={{ position: 'absolute', top: 14, right: 18, fontSize: '0.6875rem', color: ACCENT, fontWeight: 700 }}>
                  <InlineSpinner isLoading /> 更新中
                </div>
              )}
            </div>

            {/* 明細 */}
            <div style={{ marginTop: '14px' }}>
              <CardBase>
                <div style={{ padding: '14px 18px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', borderBottom: `1px solid ${LINE}` }}>
                  <CategorySegments data={data} selected={selected} onSelect={setSelected} order={order} />
                  <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                    <button type="button" onClick={() => setDetailExpanded((x) => !x)} style={{
                      cursor: 'pointer', border: `1px solid ${detailExpanded ? ACCENT : '#e2dfda'}`, borderRadius: 8,
                      background: detailExpanded ? ACCENT_BG : '#fff', color: detailExpanded ? ACCENT : INK_SOFT,
                      fontWeight: 700, fontSize: '0.75rem', padding: '6px 12px', whiteSpace: 'nowrap',
                    }}>{detailExpanded ? '高さを戻す' : '全業者を表示(高さ拡大)'}</button>
                    {selectedGroup && (
                      <div style={{ fontSize: '0.75rem', color: INK_SOFT, ...numCss }}>
                        {selected} 区分計 <b style={{ color: INK }}>{yen(totalValue(selectedGroup.subtotal.total, statusMode))}</b>
                      </div>
                    )}
                  </div>
                </div>
                <DetailTable group={selectedGroup} monthKeys={data.header.month_keys} monthLabels={data.header.month_labels} status={statusMode} majorOnly={majorOnly} onCellClick={onCellClick} onToggleCheck={onToggleCheck} expanded={detailExpanded} />
              </CardBase>
            </div>

            {/* フッタ注記 (控えめ) */}
            <div style={{ marginTop: '10px', display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: '0.6875rem', color: FAINT }} className="pa-no-print">
              <span><span style={{ color: ACCENT, fontWeight: 700 }}>■</span> 未払が残る月</span>
              <span><span style={{ color: PAID, fontWeight: 700 }}>■</span> 支払済</span>
              <span><span style={{ color: INK, fontWeight: 700 }}>■</span> 当月計上</span>
              <span><span style={{ color: CHECK_OK, fontWeight: 800 }}>✓</span> チェック済み（セルにマウスを乗せると ☐ で切替）</span>
              <span><span style={{ color: CHECK_STALE, fontWeight: 800 }}>⚠</span> チェック後に取引の変動あり（金額・件数の変化を検知）</span>
              {complexCount > 0 && <span>取引先未設定（複合振込など）{complexCount}件は業者別集計の対象外</span>}
              <span style={{ marginLeft: 'auto' }}>集計 {data.header.generated_at ? new Date(data.header.generated_at).toLocaleString('ja-JP') : '—'}</span>
            </div>
          </>
        )}
      </Container>

      {toast && (
        <div style={{ position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', zIndex: 1100, minWidth: 280 }}>
          <MessageBlock success>{toast}</MessageBlock>
        </div>
      )}

      {/* セルクリック: 取引明細モーダル (複式簿記 + 証憑 + 修正指示) */}
      {dealModal && (
        <DealModal title={dealModal.title} dealIds={dealModal.dealIds} onClose={() => setDealModal(null)} flash={flashToast} />
      )}

      {/* 区分の管理 (追加/削除/順番入替) */}
      {catMgrOpen && (
        <CategoryManager cats={catDefs} flash={flashToast} setError={setError}
          onChanged={() => { loadCategories(); load(); }} onClose={() => setCatMgrOpen(false)} />
      )}

      {assignOpen && (
        <VendorAssignPanel vendors={vendors} loading={vendorsLoading} counts={vendorCounts} cats={dynamicCats} savingPartnerId={savingPartnerId} bulkRunning={bulkRunning}
          onAssign={handleAssign} onClear={handleClear} onBulk={handleBulk} onClose={closeAssign} />
      )}
      </div>

      {/* 印刷専用 完全レポート (画面非表示・@media print でのみ表示) */}
      {data && <PrintReport data={data} status={statusMode} order={order} />}
    </div>
  );
};

export default PaymentAgingPage;
